import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Empty, Modal, Select, Spin, message } from "antd";
import {
  CloseOutlined,
  MailOutlined,
  SendOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { getCrmReportPreview, sendCrmReport } from "./controller";

/**
 * Reviewing the reports that are about to go out.
 *
 * Deliberately shaped as an OUTBOX rather than a table with a preview button: the
 * question a super-admin is answering here is "is this alright to send to a
 * customer", and that question is answered by reading the letter, not by scanning
 * a row. So the letter is the hero — a sheet of paper on a tinted ground — and the
 * clinic list is a rail you flip through with ↑/↓.
 *
 * The HTML is the SERVER's, byte for byte what the clinic receives. Re-rendering
 * the figures in JSX would give us a preview that drifts from the send, which is
 * worse than no preview at all.
 *
 * Blocked clinics are listed and readable too. Seeing the report a clinic is NOT
 * getting is how you notice the one contact nobody approved.
 */

const PERIODS = [
  { value: "monthly", label: "This month" },
  { value: "weekly", label: "This week" },
];

export default function OfficeReportsReviewDrawer({ open, rows, onClose, onSent }) {
  const ready = useMemo(() => (rows || []).filter((r) => !r.blocked_reason), [rows]);
  const blocked = useMemo(() => (rows || []).filter((r) => r.blocked_reason), [rows]);
  const ordered = useMemo(() => ready.concat(blocked), [ready, blocked]);

  const [activeId, setActiveId] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentIds, setSentIds] = useState([]);

  // Only the newest preview may paint — flipping the rail quickly would otherwise
  // let a slow earlier response land on top of a later one.
  const ticket = useRef(0);

  const active = useMemo(
    () => ordered.find((r) => r.office_id === activeId) || null,
    [ordered, activeId],
  );

  // Pick a starting clinic ONCE per opening. `ordered` is derived from rows, and
  // sending refetches rows — so a plain [open, ordered] effect would re-fire after
  // every send, wiping the "sent" markers and throwing the reviewer back to the
  // top of a queue they were working down.
  const initialised = useRef(false);
  useEffect(function () {
    if (!open) { initialised.current = false; return; }
    if (initialised.current || !ordered.length) return;
    initialised.current = true;
    setSentIds([]);
    setActiveId(ordered[0].office_id);
  }, [open, ordered]);

  const load = useCallback(
    async function (officeId, per) {
      if (!officeId) { setPreview(null); return; }
      const mine = ++ticket.current;
      setLoading(true);
      const res = await getCrmReportPreview(officeId, per);
      if (mine !== ticket.current) return;
      const ok = res.status >= 200 && res.status < 300 && res.json;
      // An error is not an empty report — see the empty-state lesson in
      // CrmSettings: never state something we haven't established.
      setFailed(!ok);
      setPreview(ok ? res.json : null);
      setLoading(false);
    },
    [],
  );

  useEffect(function () { load(activeId, period); }, [activeId, period, load]);

  // ↑/↓ and j/k walk the queue. A review surface lives or dies on how fast you can
  // get through it.
  useEffect(function () {
    if (!open) return undefined;
    function onKey(e) {
      if (!ordered.length) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const dir = (e.key === "ArrowDown" || e.key === "j") ? 1
        : (e.key === "ArrowUp" || e.key === "k") ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      const i = ordered.findIndex((r) => r.office_id === activeId);
      const next = Math.max(0, Math.min(ordered.length - 1, (i === -1 ? 0 : i) + dir));
      setActiveId(ordered[next].office_id);
    }
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [open, ordered, activeId]);

  function confirmSend() {
    const to = (preview && preview.recipients) || [];
    Modal.confirm({
      title: `Send to ${active.office_name}?`,
      // Named, not counted. This is the last moment before a customer receives a
      // claim about our own response times.
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>
            From <strong>{preview.sender_email}</strong> to:
          </p>
          <ul className="rev-tolist">
            {to.map((r) => (
              <li key={r.email}>{r.name ? `${r.name} · ${r.email}` : r.email}</li>
            ))}
          </ul>
        </div>
      ),
      okText: "Send",
      onOk: doSend,
    });
  }

  async function doSend() {
    setSending(true);
    try {
      const res = await sendCrmReport({ office_id: active.office_id, period });
      if (res.status >= 200 && res.status < 300) {
        message.success(`Sent to ${active.office_name}.`);
        setSentIds((s) => s.concat([active.office_id]));
        onSent();
        // Move on to the next unsent one — this is a queue, not a detail page.
        const rest = ordered.filter(
          (r) => !r.blocked_reason && r.office_id !== active.office_id
            && sentIds.indexOf(r.office_id) === -1,
        );
        if (rest.length) setActiveId(rest[0].office_id);
      } else {
        message.error((res.json && res.json.detail) || "Couldn't send that report.");
      }
    } finally {
      setSending(false);
    }
  }

  function railItem(r, isBlocked) {
    const sent = sentIds.indexOf(r.office_id) !== -1;
    return (
      <button
        type="button"
        key={r.office_id}
        className={
          "rev-item" +
          (r.office_id === activeId ? " is-active" : "") +
          (isBlocked ? " is-blocked" : "") +
          (sent ? " is-sent" : "")
        }
        onClick={() => setActiveId(r.office_id)}
      >
        <span className="rev-item-top">
          <span className="rev-item-name">{r.office_name}</span>
          {isBlocked ? <WarningOutlined className="rev-item-flag" /> : null}
        </span>
        <span className="rev-item-sub">
          {sent ? "Sent just now"
            : isBlocked ? r.blocked_reason
              : `${r.recipient_count} recipient${r.recipient_count === 1 ? "" : "s"}`}
        </span>
      </button>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closable={false}
      title={null}
      width="min(980px, 94vw)"
      destroyOnClose
      className="crm-drawer rev-drawer"
      bodyStyle={{ padding: 0, background: "#f7f6fb" }}
    >
      <button className="crm-hero-close" onClick={onClose} aria-label="Close">
        <CloseOutlined />
      </button>

      <div className="rev-hero">
        <div className="rev-hero-icon"><MailOutlined /></div>
        <div className="rev-hero-text">
          <div className="rev-hero-title">Ready to send</div>
          <div className="rev-hero-sub">
            {ready.length
              ? `${ready.length} clinic${ready.length === 1 ? "" : "s"} ready`
              : "Nothing can be sent yet"}
            {blocked.length ? ` · ${blocked.length} held back` : ""}
          </div>
        </div>
        <Select
          value={period}
          onChange={setPeriod}
          options={PERIODS}
          style={{ width: 150 }}
        />
      </div>

      {!ordered.length ? (
        <div className="rev-blank">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No clinic has an FTC assigned to correspond with it yet, so there is nothing to send."
          />
        </div>
      ) : (
        <div className="rev-body">
          <div className="rev-rail">
            {ready.length ? (
              <>
                <div className="rev-rail-head">Ready · {ready.length}</div>
                {ready.map((r) => railItem(r, false))}
              </>
            ) : null}
            {blocked.length ? (
              <>
                <div className="rev-rail-head rev-rail-head-muted">
                  Won&rsquo;t send · {blocked.length}
                </div>
                {blocked.map((r) => railItem(r, true))}
              </>
            ) : null}
            {!ready.length && blocked.length ? (
              <p className="rev-rail-hint">
                Each of these is missing something. Open one to read the report it
                would have sent, and fix the cause under
                <strong> FTC Users &rarr; Correspondence</strong>.
              </p>
            ) : null}
          </div>

          <div className="rev-stage">
            {loading ? (
              <div className="rev-center"><Spin /></div>
            ) : failed ? (
              <div className="rev-center">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <>
                      <div>Couldn&rsquo;t build this preview.</div>
                      <div className="rev-note">
                        The server returned an error — the report itself may be fine.
                      </div>
                    </>
                  }
                >
                  <Button size="small" onClick={() => load(activeId, period)}>Try again</Button>
                </Empty>
              </div>
            ) : !preview ? null : (
              /* Keyed by clinic so switching replays the reveal — the letter reads
                 as being placed down rather than swapped in place. */
              <div className="rev-letter" key={`${preview.office_id}:${period}`}>
                <div className="rev-envelope">
                  <div className="rev-field">
                    <span className="rev-label">From</span>
                    <span className="rev-value rev-addr">
                      {preview.sender_email || <em>nobody assigned</em>}
                    </span>
                  </div>
                  <div className="rev-field">
                    <span className="rev-label">To</span>
                    <span className="rev-value rev-addr">
                      {preview.recipients && preview.recipients.length
                        ? preview.recipients.map((r) => r.email).join(", ")
                        : <em>no approved contacts</em>}
                    </span>
                  </div>
                  <div className="rev-field">
                    <span className="rev-label">Subject</span>
                    <span className="rev-value rev-subject">{preview.subject}</span>
                  </div>
                </div>

                <div className="rev-paper">
                  {/* The server's own HTML — what is previewed IS what is sent. */}
                  <div dangerouslySetInnerHTML={{ __html: preview.html }} />
                </div>

                <div className="rev-actions">
                  {preview.blocked_reason ? (
                    <div className="rev-blocked">
                      <WarningOutlined /> {preview.blocked_reason}
                    </div>
                  ) : (
                    <div className="rev-ok">
                      {preview.enabled
                        ? "Scheduled sending is on for this clinic."
                        : "Scheduled sending is off — this would only go if sent by hand."}
                    </div>
                  )}
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sending}
                    disabled={!!preview.blocked_reason}
                    onClick={confirmSend}
                  >
                    Send this report
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
